import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const AuditLogCard = ({ auditLog }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filter, setFilter] = useState('ALL');

  const getActionIcon = (action) => {
    const icons = {
      CREATED: 'Plus',
      UPDATED: 'Edit',
      STATUS_CHANGED: 'ArrowRight',
      ASSIGNED: 'UserCheck',
      UNASSIGNED: 'UserX',
      FILE_UPLOADED: 'Upload',
      FILE_DELETED: 'Trash2',
      NOTE_ADDED: 'MessageSquare',
      INVOICE_GENERATED: 'FileText'
    };
    return icons?.[action] || 'Activity';
  };

  const getActionColor = (action) => {
    const colors = {
      CREATED: 'text-green-600',
      UPDATED: 'text-blue-600',
      STATUS_CHANGED: 'text-purple-600',
      ASSIGNED: 'text-emerald-600',
      UNASSIGNED: 'text-orange-600',
      FILE_UPLOADED: 'text-cyan-600',
      FILE_DELETED: 'text-red-600',
      NOTE_ADDED: 'text-indigo-600',
      INVOICE_GENERATED: 'text-amber-600'
    };
    return colors?.[action] || 'text-text-secondary';
  };

  const getActionLabel = (action) => {
    const labels = {
      CREATED: 'Erstellt',
      UPDATED: 'Aktualisiert',
      STATUS_CHANGED: 'Status geändert',
      ASSIGNED: 'Zugewiesen',
      UNASSIGNED: 'Zuweisung aufgehoben',
      FILE_UPLOADED: 'Datei hochgeladen',
      FILE_DELETED: 'Datei gelöscht',
      NOTE_ADDED: 'Notiz hinzugefügt',
      INVOICE_GENERATED: 'Rechnung erstellt'
    };
    return labels?.[action] || action;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filterOptions = [
    { value: 'ALL', label: 'Alle Aktivitäten' },
    { value: 'STATUS_CHANGED', label: 'Statusänderungen' },
    { value: 'ASSIGNED', label: 'Zuweisungen' },
    { value: 'FILE_UPLOADED', label: 'Datei-Uploads' },
    { value: 'NOTE_ADDED', label: 'Notizen' }
  ];

  const filteredLog = filter === 'ALL' 
    ? auditLog 
    : auditLog?.filter(entry => entry?.action === filter);

  const displayedLog = isExpanded ? filteredLog : filteredLog?.slice(0, 5);

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
          <Icon name="Activity" size={20} />
          Aktivitätsprotokoll
        </h3>
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e?.target?.value)}
            className="text-sm border border-input rounded-lg px-3 py-1"
          >
            {filterOptions?.map(option => (
              <option key={option?.value} value={option?.value}>
                {option?.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="space-y-3">
        {displayedLog?.length === 0 ? (
          <div className="text-center py-8 text-text-secondary">
            <Icon name="Activity" size={48} className="mx-auto mb-3 opacity-50" />
            <p>Keine Aktivitäten gefunden</p>
            <p className="text-sm">Für den ausgewählten Filter sind keine Einträge vorhanden.</p>
          </div>
        ) : (
          displayedLog?.map((entry, index) => (
            <div key={entry?.id} className="flex gap-3 p-3 border border-border rounded-lg">
              {/* Timeline Indicator */}
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-muted ${getActionColor(entry?.action)}`}>
                  <Icon name={getActionIcon(entry?.action)} size={14} />
                </div>
                {index < displayedLog?.length - 1 && (
                  <div className="w-0.5 h-6 bg-border mt-2" />
                )}
              </div>

              {/* Entry Content */}
              <div className="flex-1">
                <div className="flex items-start justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-text-primary">
                      {getActionLabel(entry?.action)}
                    </span>
                    {entry?.details && (
                      <span className="text-sm text-text-secondary">
                        • {entry?.details}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-text-secondary">
                    {formatTimestamp(entry?.timestamp)}
                  </span>
                </div>

                <div className="flex items-center gap-2 text-sm text-text-secondary">
                  <div className="w-5 h-5 rounded-full overflow-hidden">
                    <Image
                      src={`https://randomuser.me/api/portraits/men/${entry?.userId % 50}.jpg`}
                      alt={entry?.userName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span>{entry?.userName}</span>
                  {entry?.ipAddress && (
                    <>
                      <span>•</span>
                      <span className="font-mono text-xs">{entry?.ipAddress}</span>
                    </>
                  )}
                </div>

                {entry?.changes && (
                  <div className="mt-2 p-2 bg-muted rounded text-xs">
                    <div className="font-medium text-text-primary mb-1">Änderungen:</div>
                    {Object.entries(entry?.changes)?.map(([field, change]) => (
                      <div key={field} className="text-text-secondary">
                        <span className="font-medium">{field}:</span>{' '}
                        <span className="text-red-600">{change?.from}</span>{' '}
                        → <span className="text-green-600">{change?.to}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        )}

        {/* Show More/Less Button */}
        {filteredLog?.length > 5 && (
          <div className="text-center pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
              iconPosition="right"
            >
              {isExpanded 
                ? 'Weniger anzeigen' 
                : `${filteredLog?.length - 5} weitere Einträge anzeigen`
              }
            </Button>
          </div>
        )}

        {/* Export Options */}
        <div className="pt-4 border-t border-border">
          <div className="flex gap-2 text-sm">
            <Button
              variant="ghost"
              size="sm"
              iconName="Download"
              iconPosition="left"
            >
              Protokoll exportieren
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconName="Printer"
              iconPosition="left"
            >
              Drucken
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogCard;